import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";
import CryptoJS from "crypto-js";

const CRYPTO_KEY = "Mtoken";
const BASE_URL = process.env.REACT_APP_API_URL ?? "https://service.mutfacikservices.com/api/";

function encryptData(plain: string) {
  return CryptoJS.AES.encrypt(plain, CRYPTO_KEY).toString();
}

function decryptData(cipher: string) {
  return CryptoJS.AES.decrypt(cipher, CRYPTO_KEY).toString(CryptoJS.enc.Utf8);
}

function readStoredToken(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    return JSON.parse(decryptData(raw));
  } catch {
    return null;
  }
}

function writeStoredToken(data: Record<string, unknown>) {
  localStorage.setItem("user", encryptData(JSON.stringify(data)));
}

function forceLogout() {
  const stored = readStoredToken();
  localStorage.removeItem("user");
  window.location.replace(
    stored?.isDriverOnly ? "/DriverLogin" : stored?.isWaiterOnly ? "/WaiterLogin" : "/"
  );
}

/* ── Paylaşılan refresh durumu (tüm instance'lar ve ensureFreshToken çağrıları için) ── */
let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function flushQueue(err: unknown, token: string | null) {
  pendingQueue.forEach((p) => (err ? p.reject(err) : p.resolve(token!)));
  pendingQueue = [];
}

/* Gerçek refresh çağrısı + kuyruk koordinasyonu. Aynı anda tetiklenen birden fazla
   çağrı (bir 401 interceptor'ı ile bir route guard aynı saniyede) tek istekte birleşir. */
function refreshAndQueue(refreshToken: string): Promise<string> {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      pendingQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  return axios
    .post<{
      token: string;
      expiration: string;
      refreshToken: string;
      refreshTokenExpiration: string;
      restourantId?: string;
    }>(`${BASE_URL}Auth/RefreshToken`, { refreshToken })
    .then((res) => {
      const stored = readStoredToken() ?? {};
      // Refresh yanıtında restourantId gelmez (null) — garson/kurye/admin oturumlarında girişte
      // alınan restoranı ezmesin; yalnızca dolu gelirse güncelle.
      const { restourantId, ...rest } = res.data;
      writeStoredToken({ ...stored, ...rest, ...(restourantId ? { restourantId } : {}) });
      flushQueue(null, res.data.token);
      return res.data.token;
    })
    .catch((err) => {
      flushQueue(err, null);
      throw err;
    })
    .finally(() => {
      isRefreshing = false;
    });
}

/* ── İnterceptor'ı verilen instance'a bağla ── */
export function attachRefreshInterceptor(instance: AxiosInstance) {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original: InternalAxiosRequestConfig & { _retry?: boolean } = error.config;

      /* 401 değilse veya zaten retry ediliyorsa doğrudan hata fırlat */
      if (error.response?.status !== 401 || !original || original._retry) {
        return Promise.reject(error);
      }

      original._retry = true;

      const stored = readStoredToken();
      if (!stored?.refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      try {
        const newToken = await refreshAndQueue(stored.refreshToken as string);
        original.headers.Authorization = `Bearer ${newToken}`;
        return instance(original);
      } catch (refreshError) {
        forceLogout();
        return Promise.reject(refreshError);
      }
    }
  );
}

/**
 * Erişim token'ının süresi geçmişse ve refresh token hâlâ geçerliyse sessizce yeniler.
 * Route guard'lar ve açılış kontrolleri, bir 401 beklemeden bunu çağırabilir.
 *
 * - true  → kullanıcı oturumda kalabilir (token zaten geçerliydi veya az önce yenilendi)
 * - false → oturum gerçekten bitmiş; çağıran taraf logout akışını başlatmalı
 */
export async function ensureFreshToken(): Promise<boolean> {
  const stored = readStoredToken();
  if (!stored) return false;

  const expiration = stored.expiration as string | undefined;
  if (expiration && new Date(expiration) > new Date()) return true;

  const refreshToken = stored.refreshToken as string | undefined;
  const refreshTokenExpiration = stored.refreshTokenExpiration as string | undefined;
  if (!refreshToken || (refreshTokenExpiration && new Date(refreshTokenExpiration) <= new Date())) {
    return false;
  }

  try {
    await refreshAndQueue(refreshToken);
    return true;
  } catch {
    return false;
  }
}
